/**
 * Compresses an image file to be under the specified size limit
 * @param file - The original image file
 * @param maxSizeBytes - Maximum file size in bytes (default: 1MB)
 * @param quality - Initial compression quality (0-1, default: 0.8)
 * @returns Promise<File> - Compressed image file
 */
export const compressImage = async (
    file: File,
    maxSizeBytes: number = 1024 * 1024, // 1MB
    quality: number = 0.8
): Promise<File> => {
    // If file is already under the limit, return as-is
    if (file.size <= maxSizeBytes) {
        return file;
    }

    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate new dimensions to maintain aspect ratio
            let { width, height } = img;
            const maxDimension = 1920; // Reasonable max dimension

            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = (height * maxDimension) / width;
                    width = maxDimension;
                } else {
                    width = (width * maxDimension) / height;
                    height = maxDimension;
                }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw and compress
            if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                
                const tryCompress = (currentQuality: number) => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Failed to compress image'));
                                return;
                            }

                            // If still too large and quality can be reduced further
                            if (blob.size > maxSizeBytes && currentQuality > 0.1) {
                                tryCompress(currentQuality - 0.1);
                                return;
                            }

                            // Create new file with compressed blob
                            const compressedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: Date.now(),
                            });

                            resolve(compressedFile);
                        },
                        file.type,
                        currentQuality
                    );
                };

                tryCompress(quality);
            } else {
                reject(new Error('Could not get canvas context'));
            }
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        // Use FileReader to create a data URL instead of blob URL to avoid CSP issues
        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target?.result) {
                img.src = e.target.result as string;
            } else {
                reject(new Error('Failed to read file'));
            }
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
    });
};

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 